import { Link, useLocation } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import { ChevronDown, Search, FileCheck, Shield, Scale, Database, BookOpen, Mail, Gavel, BadgeCheck } from 'lucide-react';
import MobileMenu from './MobileMenu';

const NavLinks = () => {
  const location = useLocation();

  const [isSolutionsOpen, setIsSolutionsOpen] = useState(false);
  const [isResourcesOpen, setIsResourcesOpen] = useState(false);
  const solutionsDropdownRef = useRef(null);
  const resourcesDropdownRef = useRef(null);
  
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (solutionsDropdownRef.current && !solutionsDropdownRef.current.contains(event.target)) {
        setIsSolutionsOpen(false);
      }
      if (resourcesDropdownRef.current && !resourcesDropdownRef.current.contains(event.target)) {
        setIsResourcesOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Close dropdowns when route changes
  useEffect(() => {
    setIsSolutionsOpen(false);
    setIsResourcesOpen(false);
  }, [location.pathname]);

  const navItems = [
    { label: 'Platform', href: '/platform' },
    { 
      label: 'Solutions', 
      href: '/use-cases',
      dropdown: [
        {
          title: 'DSPM',
          items: [
            { 
              id: 'data-discovery',
              label: 'Data Discovery', 
              href: '/use-cases/data-discovery-and-classification',
              icon: Search
            },
            { 
              id: 'data-classification',
              label: 'Data Classification', 
              href: '/use-cases/data-discovery-and-classification',
              icon: FileCheck
            },
            { 
              id: 'data-detection-response',
              label: 'Data Detection & Response', 
              href: '/use-cases/data-detection-response',
              icon: Shield
            },
            { 
              id: 'data-compliance',
              label: 'Data Compliance', 
              href: '/use-cases/data-compliance',
              icon: Scale
            },
            { 
              id: 'data-governance',
              label: 'Data Governance', 
              href: '/use-cases/data-governance',
              icon: Database
            }
          ]
        },
        {title: 'GRC',
          items: 
          [
            {
              id: 'grc',
              label: 'Ancile GRC', 
              href: '/use-cases/ancile-grc',
              icon: Gavel  
            }
          ] 
        }
      ]
    },
    { label: 'Use Cases', href: '/use-cases' },
    { label: 'Company', href: '/company' },
    { 
      label: 'Resources', 
      href: '/resources',
      dropdown: [
        { 
          id: 'trust-center',
          label: 'Trust Center', 
          href: 'https://trust.ancile.ai/',
          icon: BadgeCheck
        },
        { 
          id: 'blog',
          label: 'Blog', 
          href: '/blog',
          icon: BookOpen
        },
        { 
          id: 'contact',
          label: 'Contact Us', 
          href: '/contact',
          icon: Mail
        },
        
      ]
    }
  ];

  // Handle clicking on dropdown menu items
  const handleDropdownClick = (e, dropdownType) => {
    // Prevent default behavior (navigation)
    e.preventDefault();
    
    if (dropdownType === 'solutions') {
      setIsSolutionsOpen(!isSolutionsOpen);
      setIsResourcesOpen(false);
    } else if (dropdownType === 'resources') {
      setIsResourcesOpen(!isResourcesOpen);
      setIsSolutionsOpen(false);
    }
  };

  return (
    <div className="flex items-center space-x-8">
      <MobileMenu navItems={navItems} />
      <nav className="hidden md:flex items-center space-x-6">
        {navItems.map((item) => (
          <div key={item.label} className="relative" ref={
            item.label === 'Solutions' ? solutionsDropdownRef : 
            item.label === 'Resources' ? resourcesDropdownRef : null
          }>
            {item.dropdown ? (
              <div className="relative">
                <div
                  className="flex items-center gap-1 cursor-pointer"
                  onClick={(e) => handleDropdownClick(
                    e, 
                    item.label === 'Solutions' ? 'solutions' : 
                    item.label === 'Resources' ? 'resources' : null
                  )}
                >
                  <span className={`text-base font-medium transition-colors hover:text-blue-500 ${
                    (item.label === 'Solutions' && location.pathname.startsWith('/use-cases')) ||
                    (item.label === 'Resources' && (location.pathname === '/blog' || location.pathname === '/contact'))
                      ? 'text-blue-500'
                      : 'text-gray-600'
                  }`}>
                    {item.label}
                  </span>
                  <ChevronDown className={`w-4 h-4 transition-transform ${
                    (item.label === 'Solutions' && isSolutionsOpen) || 
                    (item.label === 'Resources' && isResourcesOpen) 
                      ? 'rotate-180' 
                      : ''
                  }`} />
                </div>
                
                {((item.label === 'Solutions' && isSolutionsOpen) || 
                  (item.label === 'Resources' && isResourcesOpen)) && (
                  <div 
                    className="absolute top-full left-0 mt-2 w-72 bg-white rounded-lg shadow-lg border border-gray-100 py-2 z-50"
                  >
                    {item.dropdown.map((dropdownItem, i) => {
  // If it has items (like Solutions), treat as grouped
  if (dropdownItem.items && Array.isArray(dropdownItem.items)) {
    return (
      <div key={dropdownItem.title || `group-${i}`} className="px-4 py-2">
        {dropdownItem.title && (
          <div className="text-xs font-semibold text-gray-500 uppercase mb-1">
            {dropdownItem.title}
          </div>
        )}
        {dropdownItem.items.map((subItem) => {
          const Icon = subItem.icon;
          return (
            <Link
              key={subItem.id}
              to={subItem.href}
              onClick={() => {
                if (item.label === 'Solutions') setIsSolutionsOpen(false);
                if (item.label === 'Resources') setIsResourcesOpen(false);
              }}
              className={`flex items-center gap-3 px-4 py-2 text-sm transition-colors hover:bg-gray-50 cursor-pointer ${
                location.pathname === subItem.href
                  ? 'text-blue-500'
                  : 'text-gray-600'
              }`}
            >
              <div className="p-2 bg-[#EBF3FF] rounded-lg">
                <Icon className="w-5 h-5 text-[#4B83F2]" />
              </div>
              <span>{subItem.label}</span>
            </Link>
          );
        })}
      </div>
    );
  }

  // If it's a single-level item (like Resources dropdown)
  const Icon = dropdownItem.icon;
  return (
    <Link
      key={dropdownItem.id || `item-${i}`}
      to={dropdownItem.href}
      onClick={() => {
        if (item.label === 'Solutions') setIsSolutionsOpen(false);
        if (item.label === 'Resources') setIsResourcesOpen(false);
      }}
      className={`flex items-center gap-3 px-4 py-2 text-sm transition-colors hover:bg-gray-50 cursor-pointer ${
        location.pathname === dropdownItem.href
          ? 'text-blue-500'
          : 'text-gray-600'
      }`}
    >
      <div className="p-2 bg-[#EBF3FF] rounded-lg">
        <Icon className="w-5 h-5 text-[#4B83F2]" />
      </div>
      <span>{dropdownItem.label}</span>
    </Link>
  );
})}

                  </div>
                )}
              </div>
            ) : (
              <Link
                to={item.href}
                className={`text-base font-medium transition-colors hover:text-blue-500 ${
                  location.pathname === item.href
                    ? 'text-blue-500'
                    : 'text-gray-600'
                }`}
              >
                {item.label}
              </Link>
            )}
          </div>
        ))}
      </nav>
      <button
        className="px-5 py-2 text-white font-semibold bg-blue-500 rounded-lg hover:bg-blue-500 transition-colors"
        onClick={() => window.open('/contact')}
      >
        Get Demo
      </button>
    </div>
  );
};

export default NavLinks;